import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Grid,
} from '@mui/material';

const VALIDATION_MESSAGE = 'TCI and TCV are required to enter a >=50% stage.';

const isValid = (v) => v !== '' && v !== null && v !== undefined && !isNaN(Number(v)) && Number(v) >= 0;

const emptyValues = {
  tci: '',
  total_contract_value: '',
  one_time_saas: '',
  mrr: '',
};

/**
 * Shared dialog for capturing deal financials (TCI/TCV/OneTime(SaaS)/MRR)
 * with a read-only derived ARR, used when a deal enters a >=50% stage.
 *
 * Props:
 *  - open: bool
 *  - currencyLabel: string|undefined
 *  - targetStageName: string
 *  - initialValues: { tci, total_contract_value, one_time_saas, mrr } (strings; '' allowed)
 *  - onConfirm: (values) => void  // values: same 4 keys, strings
 *  - onCancel: () => void
 */
const FinancialCaptureDialog = ({
  open,
  currencyLabel,
  targetStageName,
  initialValues,
  onConfirm,
  onCancel,
}) => {
  const [tci, setTci] = useState('');
  const [totalContractValue, setTotalContractValue] = useState('');
  const [oneTimeSaas, setOneTimeSaas] = useState('');
  const [mrr, setMrr] = useState('');

  useEffect(() => {
    if (open) {
      const values = initialValues || emptyValues;
      setTci(values.tci !== undefined && values.tci !== null ? values.tci : '');
      setTotalContractValue(
        values.total_contract_value !== undefined && values.total_contract_value !== null
          ? values.total_contract_value
          : ''
      );
      setOneTimeSaas(
        values.one_time_saas !== undefined && values.one_time_saas !== null ? values.one_time_saas : ''
      );
      setMrr(values.mrr !== undefined && values.mrr !== null ? values.mrr : '');
    }
  }, [open, initialValues]);

  const tciValid = isValid(tci);
  const tcvValid = isValid(totalContractValue);
  const showValidationMessage = !tciValid || !tcvValid;

  const arrValue = mrr === '' || mrr === null || isNaN(Number(mrr)) ? '' : String(Number(mrr) * 12);

  const currencyAdornment = currencyLabel
    ? { startAdornment: <InputAdornment position="start">{currencyLabel}</InputAdornment> }
    : undefined;

  const handleClose = () => {
    if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (!(tciValid && tcvValid)) return;
    onConfirm({
      tci,
      total_contract_value: totalContractValue,
      one_time_saas: oneTimeSaas,
      mrr,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enter deal financials — {targetStageName}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="TCI"
              type="number"
              fullWidth
              value={tci}
              onChange={(e) => setTci(e.target.value)}
              error={!tciValid}
              helperText={!tciValid ? VALIDATION_MESSAGE : ''}
              InputProps={currencyAdornment}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="TCV"
              type="number"
              fullWidth
              value={totalContractValue}
              onChange={(e) => setTotalContractValue(e.target.value)}
              error={!tcvValid}
              helperText={!tcvValid ? VALIDATION_MESSAGE : ''}
              InputProps={currencyAdornment}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="OneTime (SaaS)"
              type="number"
              fullWidth
              value={oneTimeSaas}
              onChange={(e) => setOneTimeSaas(e.target.value)}
              InputProps={currencyAdornment}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="MRR"
              type="number"
              fullWidth
              value={mrr}
              onChange={(e) => setMrr(e.target.value)}
              InputProps={currencyAdornment}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="ARR"
              type="number"
              fullWidth
              value={arrValue}
              InputProps={{ ...currencyAdornment, readOnly: true }}
            />
          </Grid>
        </Grid>
        {showValidationMessage && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error">
              {VALIDATION_MESSAGE}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!(tciValid && tcvValid)}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinancialCaptureDialog;
